import React, { useMemo, memo, useState, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import {
  next,
  useLogAppVersion,
  useLogFabricProvidedDependencies,
  getFeatureFlags,
  SupportedLocale,
  Loading,
  NotFound,
  FabricApp,
  EnsureCoreData,
  _containerOnly,
} from "@granular/fabric-core";
import { GKContent, GKGranularLogo } from "@granular/gds-ux-components";

import { localDevelopmentHelpers } from "./local";
import MESSAGES from "./messages";
import { useHistoryBlocker } from "./useHistoryBlocker";

/**
 * Import our shared dependencies here so we ensure everyone uses our version.
 * Otherwise, the version will be dictated by the first app to load their
 * version of these.
 */
import "@granular/fabric-core/lib/tooling/container/sharedDepsImports";

const { loadModule, useDynamicScript } = _containerOnly;
const fabricFeatureFlags = getFeatureFlags("fabric");

const getRemoteEntryPath = (path: string) => `/assets/${path}/remoteEntry.js`;

const useLoadFabricApp = (path: string) => {
  const { isReady: isDynamicScriptReady, itFailedError: errorOfDynamicScript } =
    useDynamicScript(getRemoteEntryPath(path));

  const [FabricApp, setFabricApp] = useState<FabricApp | undefined>();
  const [isFabricAppLoading, setIsFabricAppLoading] = useState<boolean>(true);
  const [fabricAppError, setFabricAppError] = useState<ErrorEvent | null>(null);

  useEffect(() => {
    // Log any errors we encounter
    if (fabricAppError) {
      console.error(fabricAppError);
    }
  }, [fabricAppError]);

  useEffect(() => {
    const setToLoadingState = () => {
      setFabricAppError(null);
      setIsFabricAppLoading(true);
      setFabricApp(undefined);
    };
    const setToErrorState = (error: ErrorEvent) => {
      setFabricAppError(error);
      setIsFabricAppLoading(false);
      setFabricApp(undefined);
    };
    const setToSuccessState = (result: FabricApp) => {
      setFabricAppError(null);
      setIsFabricAppLoading(false);
      setFabricApp(result);
    };

    if (errorOfDynamicScript) {
      console.error(
        `Failed to load dynamic script of "${getRemoteEntryPath(path)}"`
      );
      setToErrorState(errorOfDynamicScript);
    } else if (!isDynamicScriptReady) {
      setToLoadingState();
    } else {
      setToLoadingState();

      const maybeLoadedModule = loadModule(path, "./App")();

      maybeLoadedModule
        .then((result) => {
          localDevelopmentHelpers.moduleLoadRetryManager.onSuccess();
          setToSuccessState(() => result.default);
        })
        .catch((error) => {
          localDevelopmentHelpers.moduleLoadRetryManager.onFail();
          console.error(`Failed to load module of "./App" at "${path}"`);
          setToErrorState(error);
        });
    }
  }, [path, isDynamicScriptReady, errorOfDynamicScript]);

  return {
    FabricApp,
    isFabricAppLoading,
    fabricAppError,
  };
};

export const getFederatedFabricApp: (path: string) => React.FC = (path) => {
  const FabricAppConsumer: React.FC<any> = memo((props) => {
    const { FabricApp, fabricAppError, isFabricAppLoading } =
      useLoadFabricApp(path);

    if (isFabricAppLoading) {
      return <Loading useGKContent />;
    }
    if (fabricAppError || !FabricApp) {
      return <NotFound useGKContent />;
    }

    const { requiredCoreData } = FabricApp;
    return (
      <EnsureCoreData requiredCoreData={requiredCoreData}>
        <FabricApp {...props} />
      </EnsureCoreData>
    );
  });

  FabricAppConsumer.displayName = path;

  return FabricAppConsumer;
};

export const Maintenance: React.FC<{ locale: SupportedLocale }> = ({
  locale,
}) => (
  <GKContent className="p-4" data-qa="Maintenance">
    <h1 className="text-center text-warning mt-5">
      <GKGranularLogo style={{ maxWidth: 80 }} />
    </h1>
    <h2 className="text-center">{MESSAGES.maintenance[locale].title}</h2>
    <div style={{ maxWidth: 310, margin: "auto" }}>
      <p className="text-center">{MESSAGES.maintenance[locale].message}</p>
    </div>
  </GKContent>
);

const FabricContainer: React.FC = () => {
  useLogAppVersion(process.env);
  useLogFabricProvidedDependencies(process.env);
  useHistoryBlocker();

  const locale = next.hooks.useLocale();

  const realPath = useLocation().pathname.split("/")[1];
  const path = realPath === "app" ? "insights" : realPath;

  const FabricApp = useMemo(() => {
    if (fabricFeatureFlags.isFlagEnabled("container-maintenance")) {
      // eslint-disable-next-line react/display-name
      return () => <Maintenance locale={locale} />;
    }

    if (!fabricFeatureFlags.isFlagEnabled(`container-${path}`)) {
      // eslint-disable-next-line react/display-name
      return () => <NotFound useGKContent={true} />;
    }

    return getFederatedFabricApp(path);
  }, [path, locale]);

  return (
    <Switch>
      <Route path="/" exact>
        <Redirect to="/app" />
      </Route>
      <Route>
        <FabricApp />
      </Route>
    </Switch>
  );
};

export default FabricContainer;
